/**
 * ComposicaoPreco — Bloco E.2 v2 (ADR-0019) + Fase 2 (visual .cascata fiel ao mockup).
 *
 * Cascata: custo × (1+op%) × (1+lucro%) × (1+RT%) × (1-desconto%).
 * Visual identico ao mockup (colunas CAMADA / % / DISTRIBUICAO / VALOR, selos
 * de cadeado nas camadas read-only, barras coloridas por camada, badge
 * "CALCULADO POR AREA", banner do cut-plan). A coluna VALOR mostra a
 * contribuicao INCREMENTAL de cada camada (delta entre os acumulados do backend).
 *
 * Politica de edicao preservada: custo_operacional / margem / RT sao read-only
 * (definidos pelo gestor/parceiro); so o desconto e' editavel pelo vendedor ate
 * `desconto_max_sem_aprovacao_percentual`. Acima do max: solicitar aprovacao.
 * Click na camada de custo abre o drill-down (chapas/bordas/ferragens).
 */
import React, { useEffect, useState } from "react";
import { FiAlertCircle, FiAlertTriangle, FiInfo, FiLayers } from "react-icons/fi";

import AsyncButton from "../../components/AsyncButton";
import SlideOverPanel from "../../components/SlideOverPanel";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import {
  aplicarConfigPadrao,
  atualizarOrcamento,
  solicitarAprovacaoDesconto,
} from "../../services/orcamentos";

import { fmtMoeda } from "./helpers";
import {
  ComposicaoAvisosBox,
  ComposicaoEmpty,
  ComposicaoInfoBanner,
  ComposicaoPendenteTag,
  ComposicaoSection,
  ComposicaoSubtitle,
  ComposicaoTable,
  ComposicaoTotalRow,
  Field,
  FormFooter,
  PrimaryButton,
  SecondaryButton,
  Textarea,
  WarningMsg,
} from "./styles";

function toNum(v) {
  if (v === null || v === undefined || v === "") return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function fmtPctSign(v, sign) {
  const n = Number(v || 0);
  const s = Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, "");
  return sign ? `${sign} ${s}%` : `${s}%`;
}

function clampPct(v) {
  return Math.min(100, Math.max(0, Math.abs(Number(v || 0))));
}

// SVG cadeado (camada read-only) — mesmo do mockup.
function Cadeado() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function Bar({ color, width }) {
  return (
    <span className="bar">
      <span className="fill" style={{ background: color, width: `${clampPct(width)}%` }} />
    </span>
  );
}

export default function ComposicaoPreco({ orcamento, editavel, onChange }) {
  const { request, hasAllPermissions, isMaster } = useAuth();
  const toast = useToast();

  const podeAprovarDesconto = isMaster || hasAllPermissions(["comercial:aprovar_desconto"]);
  const descontoMax = toNum(orcamento.desconto_max_sem_aprovacao_percentual);

  const [descontoEdit, setDescontoEdit] = useState(orcamento.desconto_percentual ?? "0");
  const [dirty, setDirty] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [moduloDetalhe, setModuloDetalhe] = useState(null);
  const [solicitandoAprovacao, setSolicitandoAprovacao] = useState(false);
  const [justificativa, setJustificativa] = useState("");

  useEffect(() => {
    setDescontoEdit(orcamento.desconto_percentual ?? "0");
    setDirty(false);
  }, [orcamento.desconto_percentual, orcamento.id]);

  const descontoEditNum = toNum(descontoEdit);
  const descontoEditAcimaMax = descontoMax > 0 && descontoEditNum > descontoMax;

  const handleSalvarDesconto = async () => {
    setSalvando(true);
    try {
      await atualizarOrcamento(request, orcamento.id, { desconto_percentual: descontoEdit });
      toast.success("Desconto atualizado.");
      setDirty(false);
      await onChange();
    } catch (err) {
      toast.error(err?.message || "Erro ao salvar desconto.");
    } finally {
      setSalvando(false);
    }
  };

  const handleSolicitarAprovacao = async () => {
    if (!justificativa.trim()) {
      toast.error("Justifique a solicitação de aprovação do desconto.");
      return;
    }
    try {
      await solicitarAprovacaoDesconto(request, orcamento.id, {
        desconto_solicitado: descontoEdit,
        justificativa: justificativa.trim(),
      });
      toast.success("Solicitação enviada ao gestor.");
      setSolicitandoAprovacao(false);
      setJustificativa("");
      await onChange();
    } catch (err) {
      toast.error(err?.message || "Erro ao enviar solicitação.");
    }
  };

  // Modulos CAD (drill-down)
  const ambientes = orcamento.versao_ativa?.ambientes || [];
  const modulosCad = [];
  ambientes.forEach((amb) => {
    (amb.itens || []).forEach((it) => {
      if (it.precificacao_origem === "CAD") modulosCad.push({ item: it, ambiente_nome: amb.nome });
    });
  });
  const temCad = modulosCad.length > 0;

  const rtPercent = Number(orcamento.comissao_percentual_efetiva || 0);
  const opPercent = toNum(orcamento.custo_operacional_percentual);
  const lucroPercent = toNum(orcamento.margem_lucro_percentual);

  // Valores acumulados (backend) -> contribuicao incremental de cada camada.
  const custoCad = toNum(orcamento.valor_custo_total_cad);
  const aposOp = toNum(orcamento.valor_apos_operacional);
  const aposLucro = toNum(orcamento.valor_apos_lucro);
  const aposRt = toNum(orcamento.valor_apos_rt);
  const aposDesc = toNum(orcamento.valor_apos_desconto_cad);
  const incOp = aposOp - custoCad;
  const incMargem = aposLucro - aposOp;
  const incRt = aposRt - aposLucro;
  const incDesc = aposDesc - aposRt; // negativo
  const manualTotal = toNum(orcamento.valor_manual_total);

  return (
    <>
      <div className="cascata">
        <div className="cascata__head">
          <div className="cascata__title">
            Composição de preço
            <svg className="info" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 16v-4M12 8h.01" />
            </svg>
          </div>
          {orcamento.config_padrao_difere && editavel ? (
            <AsyncButton
              variant="secondary"
              onClick={async () => { await aplicarConfigPadrao(request, orcamento.id); await onChange(); }}
              successMessage="Config padrão aplicada"
              silentError
            >
              <FiAlertCircle size={12} /> Config padrão mudou — aplicar
            </AsyncButton>
          ) : (
            <span className="tiny mono muted">{temCad ? "CALCULADO POR ÁREA" : "ITENS MANUAIS"}</span>
          )}
        </div>

        <div className="cascata__row head">
          <span className="label">CAMADA</span>
          <span className="pct">%</span>
          <span className="label" style={{ textAlign: "left" }}>DISTRIBUIÇÃO</span>
          <span className="value">VALOR</span>
        </div>

        {/* Custo material (chapas) — read-only, clicavel p/ drill-down */}
        <div className="cascata__row locked">
          <span className="label">
            <Cadeado />
            <button
              type="button"
              onClick={() => temCad && setModuloDetalhe(modulosCad[0])}
              disabled={!temCad}
              style={{ background: "none", border: 0, padding: 0, color: "inherit", cursor: temCad ? "pointer" : "default", font: "inherit", textDecoration: temCad ? "underline" : "none" }}
              title={temCad ? "Ver composição detalhada do módulo" : undefined}
            >
              Custo material (chapas){temCad ? ` · ${modulosCad.length}` : ""}
            </button>
          </span>
          <span className="pct">100%</span>
          <Bar color="var(--chart-3)" width={100} />
          <span className="value">{fmtMoeda(custoCad)}</span>
        </div>

        {/* Custo operacional — read-only */}
        <div className="cascata__row locked">
          <span className="label"><Cadeado />Custo operacional</span>
          <span className="pct">{fmtPctSign(opPercent, "+")}</span>
          <Bar color="var(--chart-4)" width={opPercent} />
          <span className="value">{fmtMoeda(incOp)}</span>
        </div>

        {/* Margem — read-only */}
        <div className="cascata__row locked">
          <span className="label"><Cadeado />Margem</span>
          <span className="pct">{fmtPctSign(lucroPercent, "+")}</span>
          <Bar color="var(--chart-2)" width={lucroPercent} />
          <span className="value">{fmtMoeda(incMargem)}</span>
        </div>

        {/* Comissão RT — vem do parceiro (read-only), estilo editable (accent) */}
        <div className="cascata__row editable">
          <span className="label">Comissão RT{rtPercent === 0 ? " (sem representante)" : ""}</span>
          <span className="pct">{fmtPctSign(rtPercent, "+")}</span>
          <Bar color="var(--chart-1)" width={rtPercent} />
          <span className="value">{fmtMoeda(incRt)}</span>
        </div>

        {/* Desconto — EDITAVEL pelo vendedor */}
        <div className="cascata__row editable">
          <span className="label">Desconto aplicado{descontoMax > 0 ? ` (max ${fmtPctSign(descontoMax)})` : ""}</span>
          <span className="pct" style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 4 }}>
            −
            <input
              type="number"
              step="0.01"
              min="0"
              max="100"
              className="pct-input"
              value={descontoEdit}
              onChange={(e) => { setDescontoEdit(e.target.value); setDirty(true); }}
              disabled={!editavel || salvando}
              style={descontoEditAcimaMax ? { borderColor: "var(--danger-solid)", color: "var(--danger-text)" } : undefined}
            />
            %
          </span>
          <Bar color="var(--chart-5)" width={descontoEditNum} />
          <span className="value" style={{ color: "var(--danger-text)" }}>{fmtMoeda(incDesc)}</span>
        </div>

        {/* Items manuais (frete/instalacao/taxas) — fora da cascata, sem markup */}
        {manualTotal > 0 && (
          <div className="cascata__row">
            <span className="label">+ Itens manuais (frete, instalação, taxas)</span>
            <span className="pct" />
            <span />
            <span className="value">+ {fmtMoeda(manualTotal)}</span>
          </div>
        )}

        {/* Total */}
        <div className="cascata__row total">
          <span className="label">Valor final ao cliente</span>
          <span className="pct" />
          <span />
          <span className="value">{fmtMoeda(orcamento.valor_total_final)}</span>
        </div>

        {temCad && (
          <div className="cascata__banner">
            <FiAlertTriangle aria-hidden="true" />
            Cálculo por área. Quando houver chapas, o cut-plan real (DinaBox) pode divergir em até 4%.
          </div>
        )}
      </div>

      {/* Salvar desconto (quando editado e dentro do limite) */}
      {dirty && editavel && !descontoEditAcimaMax && (
        <FormFooter style={{ marginTop: 10 }}>
          <SecondaryButton
            type="button"
            onClick={() => { setDescontoEdit(orcamento.desconto_percentual ?? "0"); setDirty(false); }}
            disabled={salvando}
          >
            Cancelar
          </SecondaryButton>
          <PrimaryButton type="button" onClick={handleSalvarDesconto} disabled={salvando}>
            {salvando ? "Salvando..." : "Salvar desconto"}
          </PrimaryButton>
        </FormFooter>
      )}

      {/* Desconto acima do max */}
      {descontoEditAcimaMax && editavel && (
        <WarningMsg style={{ marginTop: 10, flexDirection: "column", alignItems: "stretch", gap: 8 }}>
          <div>
            <FiAlertCircle size={14} aria-hidden="true" style={{ verticalAlign: "middle", marginRight: 4 }} />
            <strong>Desconto excede o limite</strong> de {fmtPctSign(descontoMax)} sem aprovação.{" "}
            {podeAprovarDesconto
              ? "Você pode salvar (tem permissão para aprovar desconto acima do limite)."
              : "Você não poderá enviar este orçamento sem aprovação de um gestor."}
          </div>
          <FormFooter style={{ margin: 0 }}>
            <SecondaryButton
              type="button"
              onClick={() => { setDescontoEdit(orcamento.desconto_percentual ?? "0"); setDirty(false); }}
              disabled={salvando}
            >
              Cancelar
            </SecondaryButton>
            {podeAprovarDesconto ? (
              <PrimaryButton type="button" onClick={handleSalvarDesconto} disabled={salvando}>
                {salvando ? "Salvando..." : "Salvar desconto"}
              </PrimaryButton>
            ) : (
              <PrimaryButton type="button" onClick={() => setSolicitandoAprovacao(true)} disabled={salvando}>
                Solicitar aprovação ao gestor
              </PrimaryButton>
            )}
          </FormFooter>
        </WarningMsg>
      )}

      {/* Drill-down de modulo */}
      <SlideOverPanel
        open={Boolean(moduloDetalhe)}
        onClose={() => setModuloDetalhe(null)}
        title={moduloDetalhe ? `Composição: ${moduloDetalhe.item.descricao}` : "Composicao"}
        subtitle={moduloDetalhe ? `Ambiente: ${moduloDetalhe.ambiente_nome}` : ""}
        width="min(95vw, 720px)"
      >
        {moduloDetalhe && <ComposicaoDetalhe item={moduloDetalhe.item} />}
      </SlideOverPanel>

      {/* Solicitar aprovacao de desconto */}
      <SlideOverPanel
        open={solicitandoAprovacao}
        onClose={() => { setSolicitandoAprovacao(false); setJustificativa(""); }}
        title="Solicitar aprovação de desconto"
        subtitle={`Desconto solicitado: ${fmtPctSign(descontoEditNum)} (máx sem aprovação: ${fmtPctSign(descontoMax)})`}
        width="min(95vw, 520px)"
      >
        <Field>
          Justificativa (obrigatória)
          <Textarea
            value={justificativa}
            onChange={(e) => setJustificativa(e.target.value)}
            placeholder="Ex: cliente fechou 3 cozinhas e pediu desconto extra; pagamento a vista; etc."
            rows={5}
          />
        </Field>
        <FormFooter>
          <SecondaryButton type="button" onClick={() => { setSolicitandoAprovacao(false); setJustificativa(""); }}>
            Cancelar
          </SecondaryButton>
          <PrimaryButton type="button" onClick={handleSolicitarAprovacao} disabled={!justificativa.trim()}>
            Enviar solicitação
          </PrimaryButton>
        </FormFooter>
      </SlideOverPanel>
    </>
  );
}

function ComposicaoDetalhe({ item }) {
  const composicao = (item.metadata || {}).composicao || {};
  const chapas = composicao.chapas || [];
  const bordas = composicao.bordas || [];
  const ferragens = composicao.ferragens_rateadas || [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <ComposicaoSection>
        <ComposicaoSubtitle>
          <FiLayers size={12} />
          Chapas ({chapas.length})
        </ComposicaoSubtitle>
        {chapas.length === 0 ? (
          <ComposicaoEmpty>Nenhuma chapa.</ComposicaoEmpty>
        ) : (
          <ComposicaoTable>
            <thead>
              <tr>
                <th>Material</th>
                <th className="num">m²</th>
                <th className="num">R$/m²</th>
                <th className="num">Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {chapas.map((c, i) => (
                <tr key={i}>
                  <td>
                    {c.material_name || c.material_id}
                    {c.produto_pendente && <ComposicaoPendenteTag>⚠ pendente</ComposicaoPendenteTag>}
                    {c.produto_sem_preco && <ComposicaoPendenteTag title="Cadastrado sem preço: custo zero até alguém informar.">⚠ sem preço</ComposicaoPendenteTag>}
                  </td>
                  <td className="num">{c.m2}</td>
                  <td className="num">{fmtMoeda(c.custo_unitario)}</td>
                  <td className="num">{fmtMoeda(c.subtotal)}</td>
                </tr>
              ))}
            </tbody>
          </ComposicaoTable>
        )}
      </ComposicaoSection>

      {bordas.length > 0 && (
        <ComposicaoSection>
          <ComposicaoSubtitle>Bordas ({bordas.length})</ComposicaoSubtitle>
          <ComposicaoTable>
            <thead>
              <tr>
                <th>Lado</th>
                <th className="num">Metros</th>
                <th className="num">R$/m</th>
                <th className="num">Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {bordas.map((b, i) => (
                <tr key={i}>
                  <td>{b.lado}</td>
                  <td className="num">{b.metros}</td>
                  <td className="num">{fmtMoeda(b.custo_unitario)}</td>
                  <td className="num">{fmtMoeda(b.subtotal)}</td>
                </tr>
              ))}
            </tbody>
          </ComposicaoTable>
        </ComposicaoSection>
      )}

      {ferragens.length > 0 && (
        <ComposicaoSection>
          <ComposicaoSubtitle>Ferragens rateadas ({ferragens.length})</ComposicaoSubtitle>
          <ComposicaoTable>
            <thead>
              <tr>
                <th>Ferragem</th>
                <th className="num">Qt rateada</th>
                <th className="num">R$/un</th>
                <th className="num">Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {ferragens.map((f, i) => (
                <tr key={i}>
                  <td>
                    {f.nome || f.ferragem_id}
                    {f.produto_pendente && <ComposicaoPendenteTag>⚠ pendente</ComposicaoPendenteTag>}
                    {f.produto_sem_preco && <ComposicaoPendenteTag title="Cadastrado sem preço: custo zero até alguém informar.">⚠ sem preço</ComposicaoPendenteTag>}
                  </td>
                  <td className="num">{f.qt_rateada}</td>
                  <td className="num">{fmtMoeda(f.custo_unitario)}</td>
                  <td className="num">{fmtMoeda(f.subtotal_rateado)}</td>
                </tr>
              ))}
            </tbody>
          </ComposicaoTable>
        </ComposicaoSection>
      )}

      <ComposicaoTotalRow>
        <span>Custo total do módulo</span>
        <span>{fmtMoeda(composicao.custo_total_modulo || item.valor_unitario)}</span>
      </ComposicaoTotalRow>

      {chapas.length > 0 && (
        <ComposicaoInfoBanner>
          <FiInfo size={12} aria-hidden="true" style={{ marginRight: 6, verticalAlign: "middle" }} />
          <strong>Custo aproximado por área.</strong> A API do DinaBox não expõe plano de
          corte real (veio, refilo 10mm, sobras). Para otimização real, rode o plano de
          corte no DinaBox antes de comprar. Estoque corrige o desvio ao processar a NF.
        </ComposicaoInfoBanner>
      )}

      {composicao.log_avisos && composicao.log_avisos.length > 0 && (
        <ComposicaoAvisosBox>
          <strong>Avisos do import:</strong>
          <ul>
            {composicao.log_avisos.map((a, i) => (
              <li key={i}>{a}</li>
            ))}
          </ul>
        </ComposicaoAvisosBox>
      )}
    </div>
  );
}
